import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, X } from 'lucide-react';
import constant from '../../../constant';
import ChangePasswordForm from '../../ui/changePasswordForm';

const FloatingAlert = ({ message, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md shadow-md z-50">
            <div className="flex items-center justify-between">
                <span className="block sm:inline">{message}</span>
                <button onClick={onClose} className="ml-4 focus:outline-none">
                    <X className="h-4 w-4 text-green-700 opacity-75 hover:opacity-100" />
                </button>
            </div>
        </div>
    );
};


const ChangePassword = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [formData, setFormData] = useState({
        oldPassword: '',
        newPassword: '',
    });

    const handleCancel = () => {
        navigate(-1);
    };

    const handleSubmit = async (formData) => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        const token = localStorage.getItem('token');

        try {
            const response = await fetch(`${constant.REACT_APP_API_URL}/user/change-password`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to change password. Status: ${response.status}`);
            }

            setSuccessMessage('Password changed successfully!');
            setFormData({
                oldPassword: '',
                newPassword: '',
            });

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSuccess = () => {
        setSuccessMessage(null);
    };


    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return <div className="text-center py-10 text-red-500">{error}</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6 relative">
            {successMessage && (
                <FloatingAlert message={successMessage} onClose={handleCloseSuccess} />
            )}

            <div className="max-w-3xl mx-auto pt-16 md:pt-0">
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex items-center space-x-4">
                        <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700 transition-colors">
                            <ArrowLeft className="h-6 w-6" />
                        </button>
                        <h2 className="text-2xl font-bold text-gray-800">Change Password</h2>
                    </div>
                </div>

                <ChangePasswordForm
                    initialFormData={formData}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
            </div>
        </div>
    );
};

export default ChangePassword;